<template>
  <div class="list-search">
    <form>
      <div class="row row-clear-sm-6-md-4">
        <div class="col-sm-6 col-md-4">
          <div class="form-group">
            <label class="sr-only">Keywords</label>

            <input name="keywords" v-model="stateKeywords" type="text" class="form-control" placeholder="Keywords" />
          </div>
        </div>
        <div class="col-sm-6 col-md-4">
          <div class="form-group">
            <label class="sr-only">Request Type</label>
            <selectize
              ref="opportunity"
              class="selectize selectize-multiple"
              multiple
              name="opportunity"
              v-model="stateOpportunities"
              placeholder="Request Type"
              :options="opportunityOptions"/>
          </div>
        </div>
        <div class="col-sm-6 col-md-4">
          <div class="form-group">
            <label class="sr-only">Due</label>
            <div class="input-group">
              <flatpickr
                v-model="dateRange"
                :config="flatPickerDateConfig"
                placeholder="Date"
                class="form-control"
                name="date_range"/>

              <span class="input-group-addon" data-toggle>
                <i class="fa fa-calendar"></i>
              </span>

              <span class="input-group-addon" data-clear>
                <i class="fa fa-times"></i>
              </span>
            </div>
          </div>
        </div>
        <div class="col-sm-6 col-md-4" v-if="$costReferences" >
          <div class="form-group">
            <label class="sr-only">Amount</label>
            <div class="row">
              <div class="col-xs-6">
                <input name="amount_low" v-model="stateAmountLow" type="text" class="form-control" placeholder="Amount Low" />
              </div>
              <div class="col-xs-6">
                <input name="amount_high" v-model="stateAmountHigh" type="text" class="form-control" placeholder="Amount High" />
              </div>
            </div>
          </div>
        </div>
        <div class="col-sm-6 col-md-4">
          <div class="form-group">
            <label class="sr-only">Location</label>
            <selectize
              ref="locations"
              class="selectize selectize-multiple"
              multiple
              name="location"
              v-model="stateLocations"
              placeholder="Location"
              :options="locationOptions"/>
          </div>
        </div>
        <div class="col-sm-6 col-md-4" v-if="$userIsGlobalAdmin && regionOptions.length > 1">
          <div class="form-group">
            <label class="sr-only">Region</label>
            <selectize
              ref="regions"
              class="selectize selectize-multiple"
              multiple
              name="region"
              v-model="stateRegions"
              placeholder="Region"
              :options="regionOptions"/>
          </div>
        </div>
        <div class="col-sm-6 col-md-4" v-if="$userIsManager">
          <div class="form-group">
            <label class="sr-only">{{ $starLabelPlural | sentenceCase }}</label>
            <selectize
              ref="talent"
              class="selectize selectize-multiple"
              multiple
              name="talent"
              v-model="stateTalent"
              :placeholder="$options.filters.sentenceCase($starLabelPlural)"
              :options="talentOptions"
              :pre-load="true"
              :fetch-results="fetchTalentCallback"
              :fetch-more-results="fetchMoreTalentCallback"/>
          </div>
        </div>
        <div class="col-sm-6 col-md-4" v-if="$userIsClient || $userIsAdmin || $userIsManager">
          <div class="form-group">
            <label class="sr-only">Request Number</label>
            <input name="id" v-model="stateId" type="text" class="form-control" placeholder="Request Number" />
          </div>
        </div>
        <div class="col-sm-4" v-if="$userIsAdmin">
          <div class="form-group">
            <label class="sr-only">Assigned Admin</label>
            <selectize
                  ref="assignedAdmin"
                  class="selectize selectize-multiple"
                  multiple
                  name="assignedAdmin"
                  v-model="stateAssignedAdmin"
                  placeholder="Assigned Admin"
                  :options="adminUsers"/>
          </div>
        </div>
        <div class="col-sm-4" v-if="$userIsAdmin">
          <div class="form-group">
            <label class="sr-only">{{ $t('organisation') }}</label>
            <selectize
                  ref="client"
                  class="selectize selectize-multiple"
                  multiple
                  name="client"
                  v-model="stateClient"
                  :placeholder="$options.filters.sentenceCase($t('organisation'))"
                  :options="clientUsers"
                  :pre-load="true"
                  :fetch-results="fetchClientsCallback"
                  :fetch-more-results="fetchNextClientsCallback"/>
          </div>
        </div>
        <div class="col-sm-6 col-md-4" v-if="$userIsAdmin && !$vlastMode">
          <div class="form-group">
            <label class="sr-only">Pipeline Status</label>
            <selectize
              ref="pipeline_status"
              class="selectize selectize-multiple"
              multiple
              name="pipeline_status"
              v-model="statePipelineStatus"
              placeholder="Pipeline Status"
              :options="pipelineStatusOptions"/>
          </div>
        </div>
        <div class="col-sm-6 col-md-4" v-if="$userIsAdmin && $vlastMode">
          <div class="form-group">
            <label class="sr-only">Requested Department</label>
            <selectize
              ref="requested_department"
              class="selectize selectize-multiple"
              multiple
              name="requested_department"
              v-model="stateRequestedDepartment"
              placeholder="Department"
              :options="requestedDepartmentOptions"
              :fetch-results="getDepartments"
              :fetch-more-results="getNextDepartments"/>
          </div>
        </div>
        <div class="text-right" :class="{
          'col-sm-12': $userIsManager || $userIsClient || $userIsAdmin,
          'col-sm-6 col-md-4': $userIsTalent
        }">
          <div class="form-group">
            <div @click="trackFilterApply">
              <slot name="button"/>
            </div>

            <div class="clearfix"></div>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import { stateMapper } from 'mixins/bookings/filters'
import { mapActions, mapState } from 'vuex'
import {concat, map} from 'lodash'
import Selectize from '../ext/codium/CodiumSelectize'
import Flatpickr from 'vue-flatpickr-component'
import { http } from 'utils'
import { OpportunityTracker } from 'mixins/analytics'

export default {
  mixins: [stateMapper, OpportunityTracker],

  props: {
    setFilterMutation: {
      required: true,
      type: Function
    },
    filters: {
      required: true,
      type: Object
    },
    applyFilters: {
      required: false,
      type: Function
    }
  },

  components: {
    Selectize,
    Flatpickr
  },

  data () {
    return {
      opportunityOptions: [],
      requestedDepartmentOptions: [],
      requestedDepartmentPagination: 1,
      locationOptions: [],
      regionOptions: [],
      adminUsers: [],
      clientUsers: [],
      clientPagination: {},
      pipelineStatusOptions: [
        { id: 'unvetted', text: 'Unvetted' },
        { id: 'non_responsive', text: 'Non Responsive' },
        { id: 'needs_more_talent', text: 'Needs More Talent' },
        { id: 'awaiting_decision', text: 'Awaiting Decision' },
        { id: 'verbally_agreed', text: 'Verbally Agreed' }
      ],
      flatPickerDateConfig: {
        altInput: true,
        altFormat: 'd/m/Y',
        dateFormat: 'Y-m-d',
        wrap: true,
        mode: 'range'
      }
    }
  },

  watch: {
    stateRegions (newValue, oldValue) {
      if (newValue !== oldValue) {
        this.getLocations({ flat: true, region_ids: newValue })
          .then((response) => {
            this.locationOptions = map(response.data, location => ({ id: location.name, text: location.name }))
          })
      }
    }
  },

  computed: {
    ...mapState('talent', ['talents']),

    dateRange: {
      get () {
        if (this.stateDateFrom === null || this.stateDateTo === null) {
          return null
        }

        return [this.stateDateFrom, this.stateDateTo]
      },

      set (value) {
        if (value === null || value === undefined) {
          this.stateDateFrom = null
          this.stateDateTo = null
        } else {
          [this.stateDateFrom, this.stateDateTo] = value.split(' to ')
        }
      }
    },

    talentOptions () {
      return map(this.talents, talent => ({ id: talent.id, text: talent.name }))
    }
  },

  created () {
    if (this.$userIsAdmin && this.$vlastMode) {
      this.getDepartments()
    }

    this.getOpportunities({ level: 'all', opportunity_type: 'opportunity_type' })
      .then((response) => {
        this.opportunityOptions = map(response.data, opportunity => ({ id: opportunity.id, text: opportunity.name }))
      })

    this.getLocations({ flat: true, region_ids: [this.$user.region_id] })
      .then((response) => {
        this.locationOptions = map(response.data, location => ({ id: location.name, text: location.name }))
      })

    if (this.$userIsGlobalAdmin && !this.$vlastMode) {
      this.getRegions()
        .then((response) => {
          this.regionOptions = map(response.data, region => ({id: region.id, text: region.name}))
          this.stateRegions = [this.$user.region_id]
          if (this.applyFilters) {
            this.applyFilters()
          }
        })
    }

    if (this.$userIsAdmin) {
      this.getAdminUsers()
        .then((response) => {
          this.adminUsers = map(response.data, user => ({id: user.id, text: user.name}))
          this.adminUsers.unshift({id: 0, text: 'Unassigned'})
        })
    }
  },

  methods: {
    ...mapActions('location', ['getLocations']),
    ...mapActions('region', ['getRegions']),
    ...mapActions('opportunity', ['getOpportunities']),
    ...mapActions('user', ['getAdminUsers']),
    ...mapActions('talent', ['getTalents', 'getNextTalents']),

    getDepartments (search) {
      return http.get(`departments`, { params: { search } })
        .then((response) => {
          this.requestedDepartmentOptions = response.data.data.map((item) => {
            return {
              id: item.id,
              text: item.name
            }
          })
        })
    },

    getNextDepartments (search) {
      const params = {
        search,
        page: this.requestedDepartmentPagination + 1
      }
      return http.get(`departments`, { params })
        .then((response) => {
          this.requestedDepartmentOptions = concat(this.requestedDepartmentOptions, response.data.data.map((item) => {
            return {
              id: item.id,
              text: item.name
            }
          }))
          this.requestedDepartmentPagination = response.data.meta
        })
    },

    fetchTalentCallback (name, callback) {
      return this.getTalents({ name })
        .then(() => {
          callback(this.talentOptions)
        })
    },

    fetchMoreTalentCallback (name, callback) {
      return this.getNextTalents({ name })
        .then(() => {
          callback(this.talentOptions)
        })
        .catch(() => {})
    },

    fetchClientsCallback (keywords, callback) {
      return http.get('clients/company', { params: { status: 'active', keywords } })
        .then((response) => {
          this.clientUsers = map(response.data.data, client => ({id: client.company, text: client.company}))
          this.clientPagination = response.data.meta
          callback(this.clientUsers)
        })
    },

    fetchNextClientsCallback (keywords, callback) {
      if (this.clientPagination && this.clientPagination.current_page < this.clientPagination.last_page) {
        return http.get('clients/company', { params: { status: 'active', keywords, page: this.clientPagination.current_page + 1 } })
          .then((response) => {
            const newClientUsers = map(response.data.data, client => ({id: client.company, text: client.company}))
            this.clientUsers.push(...newClientUsers)
            this.clientPagination = response.data.meta
            callback(this.clientUsers)
          })
      }
    },

    trackFilterApply () {
      // const sortOptionApplied = this.filters.sort + '-' + this.filters.order
      // const filterFields = []
      // let countApplied = 0
      // Object.keys(this.filters).forEach(key => {
      //   if (key !== 'sort' && key !== 'keywords' && key !== 'order') {
      //     filterFields.push({
      //       fieldName: key,
      //       fieldValue: this.filters[key]
      //     })
      //     if (this.filters[key] !== null && this.filters[key] !== '') {
      //       if (Array.isArray(this.filters[key])) {
      //         if (this.filters[key].length > 0) {
      //           countApplied++
      //         }
      //       } else {
      //         countApplied++
      //       }
      //     }
      //   }
      // })
      // const searchFields = [{
      //   fieldName: 'keywords',
      //   fieldValue: this.filters.keywords
      // }]
      // const pageName = this.$route.name
      // this.trackEvent(this.analytics.V2.FilterApplyEvent, { pageName, searchFields, filterFields, sortOptionApplied, countApplied })
    }
  }
}
</script>
